// INSERT conflict-tail planners (§2.4 S6b). Five ops over the already-parsed
// `structure.onConflict` (PG/SQLite `ON CONFLICT …`) and `structure.onDuplicate`
// (MySQL `ON DUPLICATE KEY UPDATE …`):
//
//   modify-insert-conflict-target   — replace / synthesize the `(…)` target group
//   modify-insert-conflict-action   — switch DO NOTHING ↔ DO UPDATE SET … (carries a SET seed)
//   add-insert-conflict-assignment  — append `, target = value` (tail-discriminated)
//   modify-insert-conflict-assignment — replace one assignment's value bytes
//   remove-insert-conflict-assignment — drop the k-th assignment with its separator
//
// target/action are ON-CONFLICT-only (ON DUPLICATE has neither). The assignment
// trio is tail-discriminated (`tail`: 'on-conflict' default | 'on-duplicate'):
// one op family, two anchors, mirroring Plan 3's `clause` discriminator. The
// anchor-separation guard returns [] when the requested tail is absent.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — modify.

import type { PlannedByteEdit, StructuralEdit, UpdateAssignment } from '../types';
import type { ConflictTailTarget } from '../types/structuralEdit';

import type { EditCtx } from './applyStructuralEdit';
import { findValueByteRange } from './applyStructuralEdit.modify.update';
import { planListInsert, planListRemove } from './applyStructuralEdit.lists';
import { insertEdit, replaceEdit } from './applyStructuralEdit.shared';

type ConflictEdit = Extract<
  StructuralEdit,
  {
    op:
      | 'modify-insert-conflict-target'
      | 'modify-insert-conflict-action'
      | 'add-insert-conflict-assignment'
      | 'modify-insert-conflict-assignment'
      | 'remove-insert-conflict-assignment';
  }
>;

export function planInsertConflictEdit(
  ctx: EditCtx,
  edit: ConflictEdit,
): readonly PlannedByteEdit[] {
  switch (edit.op) {
    case 'modify-insert-conflict-target':
      return planModifyConflictTarget(ctx, edit);
    case 'modify-insert-conflict-action':
      return planModifyConflictAction(ctx, edit);
    case 'add-insert-conflict-assignment':
      return planAddConflictAssignment(ctx, edit);
    case 'modify-insert-conflict-assignment':
      return planModifyConflictAssignment(ctx, edit);
    case 'remove-insert-conflict-assignment':
      return planRemoveConflictAssignment(ctx, edit);
  }
}

// Resolve the assignment list for a tail. Returns null (⇒ []) when the
// requested tail's clause is absent — the anchor-separation guard. Exported for
// the assignment-target swap (applyStructuralEdit.renameTargets.ts).
export function resolveAssignments(
  ctx: EditCtx,
  tail: ConflictTailTarget | undefined,
): readonly UpdateAssignment[] | null {
  if ((tail ?? 'on-conflict') === 'on-conflict') {
    const oc = ctx.structure.onConflict;
    return oc ? oc.updateAssignments ?? [] : null;
  }
  const od = ctx.structure.onDuplicate;
  return od ? od.assignments : null;
}

// ── target / action (ON CONFLICT only) ─────────────────────────────────────

function planModifyConflictTarget(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-insert-conflict-target' }>,
): readonly PlannedByteEdit[] {
  const oc = ctx.structure.onConflict;
  if (!oc) return [];
  const group = `(${edit.columnsText})`;
  if (oc.targetRange) {
    return [replaceEdit(
      ctx, oc.targetRange.startOffset, oc.targetRange.endOffset, group,
      'modify-insert-conflict-target',
    )];
  }
  // No existing target — synthesize `(…) ` just before the action keyword
  // (between CONFLICT and DO). A target-less ON CONFLICT always carries an
  // action range (target-less + action-less parses to no clause at all).
  if (oc.actionRange) {
    return [insertEdit(
      ctx, oc.actionRange.startOffset, `${group} `, 'modify-insert-conflict-target:synthesize',
    )];
  }
  return [];
}

function planModifyConflictAction(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-insert-conflict-action' }>,
): readonly PlannedByteEdit[] {
  const oc = ctx.structure.onConflict;
  if (!oc || !oc.actionRange || !oc.range) return [];
  // Replace from the action keyword through the END of the conflict clause, so
  // switching to DO NOTHING drops any DO UPDATE SET … + conflict-WHERE, and
  // switching to DO UPDATE re-seeds the SET list from scratch.
  const start = oc.actionRange.startOffset;
  const end = oc.range.endOffset;
  if (edit.action === 'do-nothing') {
    return [replaceEdit(ctx, start, end, 'DO NOTHING', 'modify-insert-conflict-action:do-nothing')];
  }
  // do-update requires a SET seed (the wrapper throws first when it is missing).
  if (edit.setText === undefined) return [];
  return [replaceEdit(
    ctx, start, end, `DO UPDATE SET ${edit.setText}`, 'modify-insert-conflict-action:do-update',
  )];
}

// ── assignment trio (both tails) ────────────────────────────────────────────

function planAddConflictAssignment(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-insert-conflict-assignment' }>,
): readonly PlannedByteEdit[] {
  const assigns = resolveAssignments(ctx, edit.tail);
  // An empty list is impossible by grammar (SET/UPDATE needs ≥1 assignment):
  // there is no anchor to append after, so guard [].
  if (!assigns || assigns.length === 0) return [];
  const text = `${edit.target} = ${edit.valueText}`;
  return [planListInsert(ctx, assigns, text, undefined, 'add-insert-conflict-assignment')];
}

function planModifyConflictAssignment(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-insert-conflict-assignment' }>,
): readonly PlannedByteEdit[] {
  const assigns = resolveAssignments(ctx, edit.tail);
  if (!assigns || edit.index < 0 || edit.index >= assigns.length) return [];
  const valueRange = findValueByteRange(ctx.source, assigns[edit.index], ctx.options);
  if (!valueRange) return [];
  return [replaceEdit(
    ctx, valueRange.start, valueRange.end, edit.newValueText, 'modify-insert-conflict-assignment',
  )];
}

function planRemoveConflictAssignment(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'remove-insert-conflict-assignment' }>,
): readonly PlannedByteEdit[] {
  const assigns = resolveAssignments(ctx, edit.tail);
  if (!assigns || edit.index < 0 || edit.index >= assigns.length) return [];
  // Removing the only assignment would leave an ill-formed empty SET/UPDATE
  // list — refuse (the UI disables it, decision-5 style).
  if (assigns.length === 1) return [];
  const planned = planListRemove(ctx, assigns, edit.index, 'remove-insert-conflict-assignment');
  return planned ? [planned] : [];
}
